"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import {
  Bookmark,
  ClipboardList,
  CreditCard,
  Minus,
  Plus,
  ShieldCheck,
  X,
} from "lucide-react";
import { StarIcon } from "@heroicons/react/20/solid";
import VariationSelector from "./VariationSelector";
import { type ComponentType } from "react";
import {
  ProductDetailData,
  ProductLabel,
  ProductLabelMeta,
  ProductVariation,
} from "../../utils/productDetail";
import { ProductImageSlider } from "../imgs";
import { cn } from "../../../../utils/cn";
import {
  pushAddToCartEvent,
  pushProductDetailEvent,
  pushRemoveFromCartEvent,
} from "../../../../analytics/dataLayer";
import {
  formatCatalogPrice,
  formatPrice as formatCurrencyPrice,
  isPriceOnRequest,
} from "../../../product-card/utils";

export interface FavoriteEntry {
  id: string;
  [key: string]: unknown;
}

export interface ProductDetailFavoriteManager {
  isHydrated?: boolean;
  isFavorite?: (favoriteId: string) => boolean;
  toggleFavorite?: (entry: FavoriteEntry) => void;
  entry?: FavoriteEntry | null;
  buildEntry?: (context: {
    product: ProductDetailData;
    variation: ProductVariation | null;
  }) => FavoriteEntry | null;
}

type ButtonProps = {
  children?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'outline' | 'ghost' | 'destructive';
  asChild?: boolean;
  [key: string]: any;
};

const DefaultButton: ComponentType<ButtonProps> = ({children, onClick, disabled, className, size = 'md', variant = 'default', asChild, ...props}) => {
  const sizeClasses = {
    sm: 'h-8 px-3 text-xs',
    md: 'h-9 px-4',
    lg: 'h-10 px-6',
  };
  const variantClasses = {
    default: 'bg-primary text-primary-foreground hover:bg-primary/90',
    outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
    ghost: 'hover:bg-accent hover:text-accent-foreground',
    destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
  };
  const Comp = asChild ? 'span' : 'button';
  return (
    <Comp
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
        sizeClasses[size],
        variantClasses[variant],
        className
      )}
      {...props}
    >
      {children}
    </Comp>
  );
};

export interface ProductDetailViewProps {
  product: ProductDetailData;
  favoriteManager?: ProductDetailFavoriteManager;
  sliderOptions?: any;
  imageFit?: 'contain' | 'cover';
  showWarrantyPaymentBlock?: boolean;
  onAddToCartSuccess?: (payload: {
    variationId: string;
    quantity: number;
  }) => void;
  onNotifyStock?: () => void;
  currency?: {
    code: string;
    symbol: string;
    position?: 'before' | 'after';
  };
  ButtonComponent?: ComponentType<ButtonProps>;
  translations?: {
    addToCart?: string;
    goToCart?: string;
    outOfStock?: string;
    notifyWhenBackInStock?: string;
    increaseQuantity?: string;
    decreaseQuantity?: string;
    aboutProduct?: string;
    sku?: string;
    allSpecifications?: string;
  };
  showDescription?: boolean;
  showAllSpecifications?: boolean;
  introText?: string;
  PriceActionsComponent?: ComponentType<{
    productName?: string;
    productCode?: string;
    currentPrice?: number;
    oldPrice?: number;
  }>;
}

const classNames = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(" ");

const iconButtonBase =
  "flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 transition hover:border-indigo-300 hover:text-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600";

const decodeHtmlEntities = (text: string | undefined | null): string => {
  if (!text) {
    return "";
  }
  return text
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, " ");
};

const toAccusativeProductName = (rawName: string): string => {
  const boundaryWords = new Set([
    "без",
    "в",
    "во",
    "для",
    "до",
    "из",
    "к",
    "на",
    "от",
    "по",
    "под",
    "при",
    "с",
    "со",
  ]);
  let canDecline = true;

  const words = rawName.trim().split(/(\s+)/).map((part) => {
    if (!part.trim()) {
      return part;
    }

    const normalized = part.toLocaleLowerCase("ru-RU").replace(/[^а-яё-]/gi, "");
    if (boundaryWords.has(normalized)) {
      canDecline = false;
      return part;
    }
    if (!canDecline) {
      return part;
    }

    return part
      .replace(/ая(?=[^а-яё]*$)/iu, "ую")
      .replace(/яя(?=[^а-яё]*$)/iu, "юю")
      .replace(/а(?=[^а-яё]*$)/iu, "у")
      .replace(/я(?=[^а-яё]*$)/iu, "ю");
  });

  const declined = words.join("");
  if (/^[А-ЯЁ][а-яё]/u.test(declined)) {
    return declined[0].toLocaleLowerCase("ru-RU") + declined.slice(1);
  }
  return declined;
};

const normalizeNumber = (value?: number | null): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  return undefined;
};

const createBaseVariation = (product: ProductDetailData): ProductVariation => ({
  id:
    product.id ||
    product.code ||
    product.sku ||
    `${product.name || "product"}-base`,
  attributes: {},
  quantity: undefined,
  price: normalizeNumber(product.price),
  priceOld: normalizeNumber(product.priceOld),
  name: product.name,
  labels: product.labels,
  picture: product.picture,
  brand: product.brand,
});

const getVariationLabels = (
  productLabels: ProductLabel[],
  selectedVariation?: ProductVariation | null
) => {
  const labelsSource =
    selectedVariation && Array.isArray(selectedVariation.labels) && selectedVariation.labels.length
      ? selectedVariation.labels
      : productLabels;

  if (!Array.isArray(labelsSource)) {
    return [];
  }

  const dedupe = new Map<string, ProductLabel>();
  labelsSource.forEach((label) => {
    if (!label) {
      return;
    }
    const code = label.code ? String(label.code) : "";
    const source = label.source ? String(label.source) : "product";
    const meta = label.meta && typeof label.meta === "object" ? label.meta : {};
    const rawValue = label.value ?? label.name ?? "";
    const valueString =
      typeof rawValue === "string" ? rawValue : String(rawValue ?? "");
    const trimmedValue = valueString.trim();
    if (!trimmedValue) {
      return;
    }

    const metaKey =
      (meta && "xml_id" in meta && String(meta.xml_id)) ||
      (meta && "enum_id" in meta && String(meta.enum_id)) ||
      (meta && "raw" in meta && String(meta.raw)) ||
      trimmedValue;
    const dedupeKey = `${code}|${metaKey}|${source}`;
    if (dedupe.has(dedupeKey)) {
      return;
    }

    const normalizedMeta: ProductLabelMeta = {};
    if (meta && typeof meta === "object") {
      Object.entries(meta).forEach(([metaKeyName, metaValue]) => {
        if (metaValue === null || metaValue === undefined) {
          return;
        }
        if (typeof metaValue === "boolean" || typeof metaValue === "number") {
          normalizedMeta[metaKeyName] = metaValue;
          return;
        }
        normalizedMeta[metaKeyName] = String(metaValue);
      });
    }

    dedupe.set(dedupeKey, {
      ...label,
      value: trimmedValue,
      name: label.name ? String(label.name) : code,
      meta: normalizedMeta,
    });
  });

  return Array.from(dedupe.values());
};

const PRODUCT_STORAGE_KEY = "cart";

const readCart = (): any[] => {
  if (typeof window === "undefined") {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(PRODUCT_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed;
  } catch {
    return [];
  }
};

const writeCart = (items: any[]) => {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.setItem(PRODUCT_STORAGE_KEY, JSON.stringify(items));
  window.dispatchEvent(new Event("cartUpdated"));
};

const getQuantityFromCart = (variationId: string): number => {
  const cart = readCart();
  const found = cart.find((item) => item && item.id === variationId);
  return found ? Number(found.quantity) || 0 : 0;
};

const ProductDetailView: React.FC<ProductDetailViewProps> = ({
  product,
  favoriteManager,
  sliderOptions,
  imageFit = 'cover',
  onAddToCartSuccess,
  onNotifyStock,
  currency,
  ButtonComponent = DefaultButton,
  translations,
  showDescription = true,
  showAllSpecifications = true,
  showWarrantyPaymentBlock = false,
  PriceActionsComponent,
  introText,
}) => {
  const t = {
    addToCart: translations?.addToCart ?? "В корзину",
    goToCart: translations?.goToCart ?? "Перейти в корзину",
    outOfStock: translations?.outOfStock ?? "Нет в наличии",
    notifyWhenBackInStock: translations?.notifyWhenBackInStock ?? "Уведомить о наличии",
    increaseQuantity: translations?.increaseQuantity ?? "Увеличить количество",
    decreaseQuantity: translations?.decreaseQuantity ?? "Уменьшить количество",
    aboutProduct: translations?.aboutProduct ?? "О товаре",
    sku: translations?.sku ?? "Артикул",
    allSpecifications: translations?.allSpecifications ?? "Все характеристики",
  };
  const hasVariations = product.variations.length > 0;
  const baseVariation = useMemo(
    () => createBaseVariation(product),
    [product]
  );

  const [selectedVariation, setSelectedVariation] = useState<ProductVariation | null>(
    hasVariations ? null : baseVariation
  );
  const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string>>({});
  const [selectionErrors, setSelectionErrors] = useState<Record<string, string>>({});
  const [quantityInCart, setQuantityInCart] = useState<number>(0);
  const [isDescriptionExpanded, setDescriptionExpanded] = useState(false);
  const [isDescriptionCollapsible, setIsDescriptionCollapsible] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isWarrantyOpen, setWarrantyOpen] = useState(false);
  const [isPaymentOpen, setPaymentOpen] = useState(false);
  const detailTrackedRef = useRef(false);
  const descriptionContentRef = useRef<HTMLDivElement | null>(null);
  const forcedDescriptionExpandedRef = useRef(false);

  const warrantyShort = "Гарантия: 1–25 лет";
  const warrantyDetails: string[] = [
    "На весь товар и изделия предоставляется гарантия 1 год",
    "На распашные и подъемные секционные ворота предоставляется гарантия 2 года",
    "На металлокаркасы зданий гарантия 5 лет. Гарантия не распространяется на лакокрасочное покрытие.",
    "На металлокаркасы зданий сухих отапливаемых помещений с учётом поддержания лакокрасочного покрытия предоставляется гарантия 25 лет.",
  ];
  const paymentTitle = "Наличный и безналичный расчет";
  const paymentDetails: string[] = [
    "Наличный и безналичный расчет",
    "Предоплата в офисе",
    "Безналичный расчет для организаций. 100% Предоплата или частичная отсрочка платежа через Сбер факторинг",
  ];

  const infoBlocks = showWarrantyPaymentBlock ? (
    <div className="mt-2 w-full rounded-lg border border-gray-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setWarrantyOpen(true)}
        className="w-full rounded-md px-3 py-2.5 text-left transition hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        aria-haspopup="dialog"
      >
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 flex-none text-green-600" />
          <div className="font-medium text-gray-900">{warrantyShort}</div>
        </div>
      </button>

      <div className="h-px w-full bg-gray-100" />

      <button
        type="button"
        onClick={() => setPaymentOpen(true)}
        className="w-full rounded-md px-3 py-2.5 text-left transition hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        aria-haspopup="dialog"
      >
        <div className="flex items-center gap-2">
          <CreditCard className="h-4 w-4 flex-none text-green-600" />
          <div className="font-medium text-gray-900">{paymentTitle}</div>
        </div>
      </button>
    </div>
  ) : null;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isWarrantyOpen || typeof document === "undefined") return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setWarrantyOpen(false);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isWarrantyOpen]);

  useEffect(() => {
    if (!isPaymentOpen || typeof document === "undefined") return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPaymentOpen(false);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isPaymentOpen]);

  useEffect(() => {
    if (!isMounted) {
      return;
    }
    if (!hasVariations) {
      setSelectedVariation(baseVariation);
      setQuantityInCart(getQuantityFromCart(baseVariation.id));
      return;
    }

    let initialAttributes: Record<string, string> = {};
    const productVariations = product.variations;
    if (typeof window !== "undefined") {
      const cart = readCart();
      const variationInCart = cart.find((cartItem) =>
        productVariations.some((variation) => variation.id === cartItem.id)
      );
      if (variationInCart && variationInCart.attributes) {
        initialAttributes = { ...variationInCart.attributes };
      }
    }
    if (Object.keys(initialAttributes).length === 0 && productVariations[0]) {
      initialAttributes = { ...productVariations[0].attributes };
    }
    setSelectedAttributes(initialAttributes);
  }, [baseVariation, hasVariations, product.variations, isMounted]);

  useEffect(() => {
    if (!isMounted || !hasVariations) {
      return;
    }
    const variation = product.variations.find((candidate) =>
      Object.entries(candidate.attributes).every(
        ([key, value]) => selectedAttributes[key] === value
      )
    );
    setSelectedVariation(variation || null);
    if (variation) {
      setQuantityInCart(getQuantityFromCart(variation.id));
    } else {
      setQuantityInCart(0);
    }
  }, [hasVariations, product.variations, selectedAttributes, isMounted]);

  useEffect(() => {
    if (!isMounted) return;
    if (detailTrackedRef.current) return;
    const target = hasVariations
      ? selectedVariation ?? baseVariation
      : baseVariation;
    if (!target) return;

    detailTrackedRef.current = true;
    pushProductDetailEvent(
      {
        ...target,
        name: decodeHtmlEntities(target.name || product.name),
        brand: product.brand,
        commerce: product.commerce,
      },
      {
        currencyCode: currency?.code,
        fallbackProduct: product,
      }
    );
  }, [
    baseVariation,
    currency?.code,
    hasVariations,
    isMounted,
    product,
    selectedVariation,
  ]);

  const favoriteEntry = useMemo(() => {
    if (favoriteManager?.buildEntry) {
      return favoriteManager.buildEntry({
        product,
        variation: selectedVariation ?? (hasVariations ? null : baseVariation),
      });
    }
    return favoriteManager?.entry ?? null;
  }, [baseVariation, favoriteManager, hasVariations, product, selectedVariation]);
  const favoriteId = favoriteEntry?.id ?? null;
  const isInFavorites =
    favoriteId && favoriteManager?.isHydrated
      ? favoriteManager?.isFavorite?.(favoriteId) ?? false
      : false;

  const handleToggleFavorite = useCallback(() => {
    if (favoriteEntry && favoriteManager?.toggleFavorite) {
      favoriteManager.toggleFavorite(favoriteEntry);
    }
  }, [favoriteEntry, favoriteManager]);

  const SLIDES = useMemo(() => {
    const slides = Array.isArray(product.imageGallery) ? product.imageGallery : [];
    if (slides.length > 0) {
      return slides;
    }
    if (product.picture) {
      return [product.picture];
    }
    return ["/placeholder.png"];
  }, [product.imageGallery, product.picture]);

  const trackAddEvent = useCallback(
    (variation: ProductVariation, quantity: number) => {
      pushAddToCartEvent(
        {
          ...variation,
          quantity,
          brand: product.brand,
          commerce: product.commerce,
        },
        { currencyCode: currency?.code, fallbackProduct: product }
      );
    },
    [currency?.code, product]
  );

  const trackRemoveEvent = useCallback(
    (variation: ProductVariation, quantity: number) => {
      pushRemoveFromCartEvent(
        {
          ...variation,
          quantity,
          brand: product.brand,
          commerce: product.commerce,
        },
        { currencyCode: currency?.code, fallbackProduct: product }
      );
    },
    [currency?.code, product]
  );

  const addToCart = useCallback(() => {
    const targetVariation = hasVariations ? selectedVariation : baseVariation;
    if (!targetVariation) {
      if (hasVariations) {
        const errors: Record<string, string> = {};
        const allAttributes = new Set<string>();
        product.variations.forEach((variation) => {
          Object.keys(variation.attributes).forEach((key) => allAttributes.add(key));
        });
        allAttributes.forEach((key) => {
          if (!selectedAttributes[key]) {
            errors[key] = `Пожалуйста, выберите ${key}`;
          }
        });
        setSelectionErrors(errors);
      }
      return;
    }

    const cart = readCart();
    const existingIndex = cart.findIndex((item) => item.id === targetVariation.id);
    let addedQuantity = 0;

    if (existingIndex > -1) {
      const existingItem = cart[existingIndex];
      const variationStock = Number(targetVariation.quantity);
      if (
        Number.isFinite(variationStock) &&
        variationStock > 0 &&
        existingItem.maxQuantity === undefined
      ) {
        existingItem.maxQuantity = variationStock;
      }
      const maxQtyCandidate = existingItem.maxQuantity ?? variationStock;
      const maxQty =
        Number.isFinite(Number(maxQtyCandidate)) && Number(maxQtyCandidate) > 0
          ? Number(maxQtyCandidate)
          : Infinity;

      if (existingItem.quantity < maxQty) {
        existingItem.quantity += 1;
        addedQuantity = 1;
      } else {
        alert("Достигнуто максимальное количество товара на складе.");
        return;
      }
      cart[existingIndex] = existingItem;
    } else {
      const cartItem = {
        ...targetVariation,
        maxQuantity:
          Number.isFinite(Number(targetVariation.quantity)) &&
          Number(targetVariation.quantity) > 0
            ? Number(targetVariation.quantity)
            : undefined,
        quantity: 1,
        picture:
          targetVariation.picture ||
          product.picture ||
          SLIDES[0] ||
          "/placeholder.png",
        name: decodeHtmlEntities(targetVariation.name || product.name),
        brand: product.brand || targetVariation.brand,
        commerce: product.commerce || targetVariation.commerce,
      };
      cart.push(cartItem);
      addedQuantity = 1;
    }

    writeCart(cart);
    setQuantityInCart(getQuantityFromCart(targetVariation.id));
    setSelectionErrors({});
    if (addedQuantity > 0) {
      trackAddEvent(targetVariation, addedQuantity);
    }
    if (onAddToCartSuccess) {
      onAddToCartSuccess({
        variationId: targetVariation.id,
        quantity: getQuantityFromCart(targetVariation.id),
      });
    }
  }, [
    SLIDES,
    baseVariation,
    hasVariations,
    onAddToCartSuccess,
    product.name,
    product.picture,
    product.variations,
    selectedAttributes,
    selectedVariation,
    trackAddEvent,
  ]);

  const increaseQuantity = useCallback(() => {
    const targetVariation = hasVariations ? selectedVariation : baseVariation;
    if (!targetVariation) {
      return;
    }
    const cart = readCart();
    const existingIndex = cart.findIndex((item) => item.id === targetVariation.id);
    if (existingIndex > -1) {
      const maxQuantity = Number(targetVariation.quantity);
      if (
        Number.isFinite(maxQuantity) &&
        cart[existingIndex].quantity >= maxQuantity
      ) {
        alert("Достигнуто максимальное количество товара на складе.");
        return;
      }
      cart[existingIndex].quantity += 1;
      writeCart(cart);
      setQuantityInCart(cart[existingIndex].quantity);
      trackAddEvent(targetVariation, 1);
    }
  }, [baseVariation, hasVariations, selectedVariation, trackAddEvent]);

  const decreaseQuantity = useCallback(() => {
    const targetVariation = hasVariations ? selectedVariation : baseVariation;
    if (!targetVariation) {
      return;
    }
    const cart = readCart();
    const existingIndex = cart.findIndex((item) => item.id === targetVariation.id);
    if (existingIndex > -1) {
      cart[existingIndex].quantity -= 1;
      const removedQty = 1;
      if (cart[existingIndex].quantity <= 0) {
        cart.splice(existingIndex, 1);
        setQuantityInCart(0);
      } else {
        setQuantityInCart(cart[existingIndex].quantity);
      }
      writeCart(cart);
      trackRemoveEvent(targetVariation, removedQty);
    }
  }, [baseVariation, hasVariations, selectedVariation, trackRemoveEvent]);

  const productLabels = useMemo(
    () => getVariationLabels(product.labels, selectedVariation ?? undefined),
    [product.labels, selectedVariation]
  );

  const hasDescription = showDescription && Boolean(product.descriptionHtml);

  // Mobile breakpoint aligned with Tailwind `sm` (640px)
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const mql = window.matchMedia("(max-width: 639px)");
    const update = () => setIsMobileViewport(mql.matches);
    update();
    const legacyMql = mql as MediaQueryList & {
      addListener?: (listener: (this: MediaQueryList, ev: MediaQueryListEvent) => any) => void;
      removeListener?: (listener: (this: MediaQueryList, ev: MediaQueryListEvent) => any) => void;
    };
    if (typeof legacyMql.addEventListener === "function") {
      legacyMql.addEventListener("change", update);
      return () => legacyMql.removeEventListener("change", update);
    }
    if (typeof legacyMql.addListener === "function") {
      legacyMql.addListener(update);
      return () => legacyMql.removeListener?.(update);
    }
    return;
  }, []);

  const collapsedDescriptionMaxHeight = isMobileViewport ? 150 : 200;

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    if (!hasDescription) {
      setIsDescriptionCollapsible(false);
      forcedDescriptionExpandedRef.current = false;
      setDescriptionExpanded(false);
      return;
    }

    const el = descriptionContentRef.current;
    if (!el) {
      return;
    }

    const recompute = () => {
      const fullHeight = el.scrollHeight;
      const nextCollapsible = fullHeight > collapsedDescriptionMaxHeight + 1;
      setIsDescriptionCollapsible(nextCollapsible);

      if (!nextCollapsible) {
        forcedDescriptionExpandedRef.current = true;
        setDescriptionExpanded(true);
      } else if (forcedDescriptionExpandedRef.current) {
        // If we previously forced expanded due to short content,
        // reset back to collapsed when it becomes collapsible again (e.g. mobile).
        forcedDescriptionExpandedRef.current = false;
        setDescriptionExpanded(false);
      }
    };

    recompute();

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => recompute());
      ro.observe(el);
    }
    window.addEventListener("resize", recompute);

    return () => {
      window.removeEventListener("resize", recompute);
      ro?.disconnect();
    };
  }, [hasDescription, product.descriptionHtml, collapsedDescriptionMaxHeight]);

  // scrollToDescription был нужен для кнопки "Перейти к описанию" в блоке shortDescription (убрана по запросу)

  useEffect(() => {
    try {
      if (typeof window === "undefined") {
        return;
      }
      const maxItems = 20;
      const cookieName = "recent_views";
      const productId = product.code;
      const categories = Array.isArray(product.recommendationSections)
        ? product.recommendationSections
        : [];

      if (!productId && categories.length === 0) {
        return;
      }

      const cookiesMap = (document.cookie || "")
        .split(";")
        .reduce<Record<string, string>>((acc, part) => {
          const [rawKey, ...rest] = part.split("=");
          const key = rawKey ? rawKey.trim() : "";
          if (!key) {
            return acc;
          }
          acc[key] = decodeURIComponent(rest.join("="));
          return acc;
        }, {});

      if (cookiesMap.recommendations_disabled === "1") return;

      let current: any[] = [];
      if (cookiesMap[cookieName]) {
        try {
          const parsed = JSON.parse(cookiesMap[cookieName]);
          if (Array.isArray(parsed)) {
            current = parsed;
          }
        } catch {
          current = [];
        }
      }

      const entry = {
        t: Date.now(),
        p: productId,
        c: Array.isArray(categories) ? categories.filter(Boolean) : [],
      };

      const filtered = current.filter((item) => item && item.p !== entry.p);
      const next = [entry, ...filtered].slice(0, maxItems);

      const value = encodeURIComponent(JSON.stringify(next));
      const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
      document.cookie = `${cookieName}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
    } catch (error) {
      console.warn("Failed to store recent views cookie", error);
    }
  }, [product.code, product.recommendationSections]);

  const priceSection = (cardClassName = "") => {
    const variation = selectedVariation ?? baseVariation;
    const currentPrice = variation?.price ?? product.price;
    const oldPrice = variation?.priceOld ?? product.priceOld;
    const comparableCurrentPrice = Number(currentPrice);
    const currentPriceOnRequest = isPriceOnRequest(currentPrice);
    const showOldPrice =
      !currentPriceOnRequest &&
      typeof oldPrice === "number" &&
      Number.isFinite(oldPrice) &&
      Number.isFinite(comparableCurrentPrice) &&
      oldPrice > comparableCurrentPrice;
    const formattedCurrentPrice = formatCatalogPrice(currentPrice, currency);
    const formattedOldPrice = showOldPrice ? formatCurrencyPrice(oldPrice, currency) : "";
    const discount =
      showOldPrice && comparableCurrentPrice > 0
        ? Math.round(((oldPrice - comparableCurrentPrice) / oldPrice) * 100)
        : null;

    return (
      <div
        className={classNames(
          "md:p-3 md:rounded-2xl shadow-none md:border price_select",
          cardClassName
        )}
      >
        <div>
          {formattedCurrentPrice ? (
            <div className="mb-3 flex items-center gap-3">
              <p className="text-xl md:text-2xl font-bold">
                {formattedCurrentPrice}
                {product.measure && !currentPriceOnRequest ? (
                  <span className="ml-2 text-lg font-normal text-gray-500">/ {product.measure}</span>
                ) : null}
              </p>
              {formattedOldPrice && (
                <p className="text-lg text-gray-500 line-through">
                  {formattedOldPrice}
                </p>
              )}
            </div>
          ) : (
            <div className="text-sm text-default-500">По запросу</div>
          )}

          {discount ? (
            <div className="mb-2">
              <span className="inline-block bg-red-100 text-red-800 text-sm font-medium px-2 py-1 rounded">
                -{discount}%
              </span>
            </div>
          ) : null}

          {variation?.quantity !== undefined && (
            <p className="hidden text-green-600">
              Наличие: {variation.quantity} шт.
            </p>
          )}
        </div>

        {hasVariations ? (
          <div className="mt-4">
            <VariationSelector
              variations={product.variations}
              selectedAttributes={selectedAttributes}
              onSelect={(next) => {
                setSelectedAttributes(next);
                setSelectionErrors((prev) => {
                  const updated = { ...prev };
                  Object.keys(next).forEach((key) => {
                    delete updated[key];
                  });
                  return updated;
                });
              }}
              errors={selectionErrors}
            />
          </div>
        ) : null}

        {isMounted && quantityInCart > 0 ? (
          <div className="mt-4 hidden md:flex items-center gap-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={decreaseQuantity}
                aria-label={t.decreaseQuantity}
                className={iconButtonBase}
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <span className="min-w-[2ch] text-center font-semibold text-sm">
                {quantityInCart}
              </span>
              <button
                type="button"
                onClick={increaseQuantity}
                aria-label={t.increaseQuantity}
                className={iconButtonBase}
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
            <ButtonComponent
              asChild
              variant="default"
              size="lg"
              className="flex-1 text-sm"
            >
              <Link href="/shop/cart">{t.goToCart}</Link>
            </ButtonComponent>
          </div>
          ) : (
            product.available ? (
            <div className="mt-4 hidden md:block">
              <ButtonComponent
                variant="default"
                size="lg"
                onClick={addToCart}
                className="w-full text-sm gap-2"
              >
                <ClipboardList className="h-5 w-5" />
                {t.addToCart}
              </ButtonComponent>
            </div>
          ) : (
            <div className="mt-4 hidden md:block">
              <ButtonComponent
                variant="outline"
                size="lg"
                onClick={onNotifyStock}
                className="w-full text-sm gap-2"
              >
                {t.notifyWhenBackInStock}
              </ButtonComponent>
            </div>
          )
        )}
      </div>
    );
  };

  return (
    <div>
      {showWarrantyPaymentBlock && isWarrantyOpen ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={() => setWarrantyOpen(false)}
            aria-label="Закрыть окно"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="warranty-modal-title"
            className="relative z-10 w-full max-w-lg rounded-xl bg-white p-5 shadow-xl ring-1 ring-black/5"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div
                  id="warranty-modal-title"
                  className="text-base font-semibold text-gray-900"
                >
                  Гарантия
                </div>
                <div className="mt-1 text-sm text-gray-600">
                  Условия гарантийного обслуживания
                </div>
              </div>
              <button
                type="button"
                onClick={() => setWarrantyOpen(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-600 transition hover:bg-gray-100 hover:text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                aria-label="Закрыть"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-gray-700">
              {warrantyDetails.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      {showWarrantyPaymentBlock && isPaymentOpen ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={() => setPaymentOpen(false)}
            aria-label="Закрыть окно"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="payment-modal-title"
            className="relative z-10 w-full max-w-lg rounded-xl bg-white p-5 shadow-xl ring-1 ring-black/5"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div
                  id="payment-modal-title"
                  className="text-base font-semibold text-gray-900"
                >
                  Оплата
                </div>
                <div className="mt-1 text-sm text-gray-600">
                  Способы оплаты
                </div>
              </div>
              <button
                type="button"
                onClick={() => setPaymentOpen(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-600 transition hover:bg-gray-100 hover:text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                aria-label="Закрыть"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-gray-700">
              {paymentDetails.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
      <div className="relative flex flex-col md:flex-row gap-4 lg:gap-x-8">
        <div className="min-w-0 md:w-5/12 md:sticky md:top-12 md:self-start">
          <ProductImageSlider slides={SLIDES} options={sliderOptions} imageFit={imageFit} />
        </div>
        <div className="min-w-0 md:w-4/12">
          <div className="flex items-start justify-between gap-3">
            <h1 className="text-xl md:text-2xl font-bold tracking-tight flex-1 min-w-0 break-words">
              {decodeHtmlEntities(product.name)}
            </h1>
            {favoriteEntry ? (
              <button
                type="button"
                onClick={handleToggleFavorite}
                aria-pressed={isInFavorites}
                className={`flex h-10 w-10 items-center justify-center rounded-full border transition-colors ${
                  isInFavorites
                    ? "border-gray-300 bg-gray-50 text-gray-900"
                    : "border-default-200 bg-white text-default-500 hover:text-gray-900"
                }`}
              >
                <span className="sr-only">
                  {isInFavorites ? "Убрать из сохранённого" : "Сохранить"}
                </span>
                <Bookmark
                  className="h-5 w-5"
                  fill={isInFavorites ? "currentColor" : "none"}
                />
              </button>
            ) : null}
          </div>
          <p className="mt-2 text-sm leading-6 text-gray-600">
            {introText ?? `Изготовим ${toAccusativeProductName(decodeHtmlEntities(product.name))} так, что будете смотреть и любоваться.`}
          </p>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-sm text-default-500">
            {product.id ? (
              <p className="line-clamp-1 whitespace-nowrap md:text-medium">
                {t.sku}: {product.id}
              </p>
            ) : null}
            <div className="flex items-center gap-2 text-gray-700">
              {product.rating ? (
                <>
                  <span>
                    {product.rating}
                    <span className="sr-only"> из 5 звезд</span>
                  </span>
                  <div className="flex items-center">
                    {[0, 1, 2, 3, 4].map((rating) => (
                      <StarIcon
                        key={rating}
                        aria-hidden="true"
                        className={classNames(
                          product.rating && product.rating > rating
                            ? "text-yellow-400"
                            : "text-gray-200",
                          "size-5 shrink-0"
                        )}
                      />
                    ))}
                  </div>
                </>
              ) : null}
              {product.reviewCount ? (
                <>
                  <span aria-hidden="true" className="text-gray-300">
                    ·
                  </span>
                  <a
                    href="#product-reviews"
                    className="font-medium text-indigo-600 transition-colors hover:text-indigo-700"
                  >
                    {product.reviewCount} отзывов
                  </a>
                </>
              ) : null}
            </div>
          </div>

          <div className="mt-4 md:hidden">
            {priceSection()}
            {infoBlocks}
            {PriceActionsComponent ? (
              <div className="mt-4">
                <PriceActionsComponent
                  productName={product.name}
                  productCode={product.code}
                  currentPrice={selectedVariation?.price ?? baseVariation?.price ?? product.price}
                  oldPrice={selectedVariation?.priceOld ?? baseVariation?.priceOld ?? product.priceOld}
                />
              </div>
            ) : null}
          </div>

          {productLabels.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {productLabels.map((label) => {
                const meta =
                  label.meta && typeof label.meta === "object" ? label.meta : {};
                const metaKey =
                  (meta && "xml_id" in meta && String(meta.xml_id)) ||
                  (meta && "enum_id" in meta && String(meta.enum_id)) ||
                  (meta && "raw" in meta && String(meta.raw)) ||
                  label.value ||
                  label.name ||
                  label.code ||
                  "label";
                const key = `${label.code || "label"}:${metaKey}:${label.source || "product"}`;
                const badgeText = decodeHtmlEntities(label.value || label.name || "");
                const titleSource =
                  (meta && "name" in meta && String(meta.name)) || label.name;
                const title =
                  titleSource && titleSource !== badgeText
                    ? decodeHtmlEntities(titleSource)
                    : undefined;
                const baseClass =
                  "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide border";
                const themedClass =
                  meta && "bg_color" in meta
                    ? ""
                    : label.source === "offer"
                    ? "bg-indigo-600/10 text-indigo-600 border-indigo-200"
                    : "bg-gray-900 text-white border-gray-900";
                const style: React.CSSProperties = {};
                if (meta && "bg_color" in meta) {
                  style.backgroundColor = String(meta.bg_color);
                  style.borderColor = String(meta.bg_color);
                }
                if (meta && "text_color" in meta) {
                  style.color = String(meta.text_color);
                }
                return (
                  <span
                    key={key}
                    title={title}
                    data-source={label.source || "product"}
                    className={classNames(baseClass, themedClass)}
                    style={style}
                  >
                    {badgeText}
                  </span>
                );
              })}
            </div>
          )}

          {product.shortDescriptionHtml || product.displayAttributes.length > 0 ? (
            <div className="mt-8">
              <div className="mb-8 property">
                {product.shortDescriptionHtml ? (
                  <div
                    className="richtext"
                    dangerouslySetInnerHTML={{
                      __html: product.shortDescriptionHtml,
                    }}
                  />
                ) : null}
                {product.displayAttributes.length > 0 ? (
                  <div className="mt-2">
                    <dl className="divide-y divide-gray-100">
                      {product.displayAttributes.map(({ key, name, value }) => (
                        <div
                          key={key}
                          className="grid grid-cols-1 min-w-0 gap-1 py-2 md:py-3 sm:grid-cols-2 sm:gap-4"
                        >
                          <dt className="min-w-0 break-words text-sm/6 font-medium text-gray-900">
                            {decodeHtmlEntities(name)}
                          </dt>
                          <dd className="min-w-0 break-words text-sm/6 text-gray-700 sm:text-left md:text-end">
                            {decodeHtmlEntities(value)}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
        <div className="md:w-3/12 md:sticky md:top-12 md:self-start">
          <div className="hidden md:block">
            {priceSection()}
            {infoBlocks}
            {PriceActionsComponent ? (
              <div className="mt-4">
                <PriceActionsComponent
                  productName={product.name}
                  productCode={product.code}
                  currentPrice={selectedVariation?.price ?? baseVariation?.price ?? product.price}
                  oldPrice={selectedVariation?.priceOld ?? baseVariation?.priceOld ?? product.priceOld}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {showDescription && hasDescription && product.descriptionHtml ? (
        <section
          id="product-description-section"
          className="mt-12"
          itemProp="description"
        >
          <div className="text-xl font-semibold text-gray-900 mb-4">
            Описание
          </div>
          <div
            id="product-description-content"
            className={`product-richtext-wrapper ${
              isDescriptionCollapsible
                ? isDescriptionExpanded
                  ? "expanded"
                  : "collapsed"
                : "expanded"
            }`}
            style={
              isDescriptionCollapsible && !isDescriptionExpanded
                ? { maxHeight: `${collapsedDescriptionMaxHeight}px` }
                : undefined
            }
          >
            <div
              className="richtext"
              ref={descriptionContentRef}
              dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
            />
          </div>
          {isDescriptionCollapsible ? (
            <button
              type="button"
              className="mt-4 text-sm font-medium text-indigo-600 hover:text-indigo-700 focus:outline-none focus-visible:ring focus-visible:ring-indigo-500/50"
              onClick={() => {
                forcedDescriptionExpandedRef.current = false;
                setDescriptionExpanded((prev) => !prev);
              }}
              aria-expanded={isDescriptionExpanded}
              aria-controls="product-description-content"
            >
              {isDescriptionExpanded ? "Свернуть" : "Показать полностью"}
            </button>
          ) : null}
        </section>
      ) : null}

      {showAllSpecifications && product.allAttributes.length > 0 && (
        <div className="mt-12">
          <div className="text-xl font-semibold text-gray-900 mb-4">
            {t.allSpecifications}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {product.allAttributes.map(({ key, name, value }) => (
              <div
                key={key}
                className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-3 last:border-none md:last:border-none"
              >
                <span className="text-sm font-medium text-gray-900 flex-shrink-0">
                  {decodeHtmlEntities(name)}
                </span>
                <span className="text-sm text-gray-700 text-right md:text-right">
                  {decodeHtmlEntities(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {product.available && isMounted ? (
        <div className="fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom,0px)+var(--bottom-bar-height,4rem))] z-40 border-t border-gray-200 bg-white px-4 py-3 shadow-lg md:hidden">
          {quantityInCart > 0 ? (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={decreaseQuantity}
                  aria-label={t.decreaseQuantity}
                  className={iconButtonBase}
                >
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span className="min-w-[2ch] text-center font-semibold text-sm">
                  {quantityInCart}
                </span>
                <button
                  type="button"
                  onClick={increaseQuantity}
                  aria-label={t.increaseQuantity}
                  className={iconButtonBase}
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
              <ButtonComponent
                asChild
                variant="default"
                size="lg"
                className="flex-1 text-sm"
              >
                <Link href="/shop/cart">{t.goToCart}</Link>
              </ButtonComponent>
            </div>
          ) : (
            product.available ? (
              <ButtonComponent
                variant="default"
                size="lg"
                onClick={addToCart}
                className="w-full text-sm gap-2"
              >
                <ClipboardList className="h-5 w-5" />
                {t.addToCart}
              </ButtonComponent>
            ) : (
              <ButtonComponent
                variant="outline"
                size="lg"
                onClick={onNotifyStock}
                className="w-full text-sm gap-2"
              >
                {t.notifyWhenBackInStock}
              </ButtonComponent>
            )
          )}
        </div>
      ) : null}
    </div>
  );
};

export default ProductDetailView;
